import React, { useMemo } from 'react';
import { Calendar, Check, ShoppingBag, Store as StoreIcon } from 'lucide-react';
import { Order } from '../../../types';
import { formatPhoneNumber } from '../../../utils/formatters';
import {
  getOrderItemAggregationKey,
  getOrderItemDisplayName,
} from '../../../utils/orderItemDisplay';

interface CompletedPickupListViewProps {
  orders: Order[];
  customerAliases?: Record<string, string>;
  onViewHistory?: (name: string, phone: string, userId?: string, orderSheetId?: string) => void;
  sortBy?: 'default' | 'name';
  searchTerm?: string;
}

const sanitizeImageUrl = (url?: string) => {
  if (!url) return '';
  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }
  return url;
};

export const CompletedPickupListView = ({
  orders,
  customerAliases,
  onViewHistory,
  sortBy,
  searchTerm,
}: CompletedPickupListViewProps) => {
  const aggregatedOrders = useMemo(() => {
    const normalizedSearch = searchTerm?.trim().toLowerCase() || '';
    const completedOrders = orders
      .filter((order) => order.status === 'COMPLETED')
      .filter((order) => {
        if (!normalizedSearch) return true;
        return (order.customerName + (order.customerPhone || ''))
          .toLowerCase()
          .includes(normalizedSearch);
      });
    const hasServerGroupedOrders = completedOrders.some(
      (order) => Array.isArray(order.aggregatedIds) && order.aggregatedIds.length > 0,
    );

    const aggregated = hasServerGroupedOrders
      ? completedOrders.map((order) => ({
          ...order,
          aggregatedIds:
            order.aggregatedIds && order.aggregatedIds.length > 0
              ? order.aggregatedIds
              : [order.id],
        }))
      : (() => {
          const map: Record<string, any> = {};

          completedOrders.forEach((order) => {
            const phoneVal = (order.customerPhone || '').replace(/\D/g, '');
            const sheetId = order.orderSheetId || order.storeId || '';
            const name = (order.customerName || '').trim();
            const key = `${sheetId}-${name}-${phoneVal}`;

            if (!map[key]) {
              map[key] = {
                ...order,
                items: [...(order.items || [])],
                aggregatedIds: [order.id],
              };
              return;
            }

            map[key].items = [...map[key].items, ...(order.items || [])];
            map[key].totalPrice =
              (map[key].totalPrice || 0) + (order.totalPrice || 0);
            map[key].aggregatedIds.push(order.id);

            if (order.request && !map[key].request) {
              map[key].request = order.request;
            } else if (
              order.request &&
              map[key].request &&
              !map[key].request.includes(order.request)
            ) {
              map[key].request += `\n${order.request}`;
            }
          });

          return Object.values(map).map((order: any) => {
            const itemMap: Record<string, any> = {};

            order.items.forEach((item: any) => {
              const productKey = getOrderItemAggregationKey(item);
              if (!itemMap[productKey]) {
                itemMap[productKey] = { ...item };
              } else {
                itemMap[productKey].quantity += item.quantity;
              }
            });

            return {
              ...order,
              items: Object.values(itemMap),
            };
          });
        })();

    if (sortBy === 'name') {
      return [...aggregated].sort((a, b) =>
        (a.customerName || '').localeCompare(b.customerName || '', 'ko-KR'),
      );
    }

    return [...aggregated].sort((a, b) => {
      const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return timeB - timeA;
    });
  }, [orders, searchTerm, sortBy]);

  const summary = useMemo(() => {
    return aggregatedOrders.reduce(
      (acc, order: any) => {
        acc.totalCustomers += 1;
        acc.totalOrders += order.aggregatedIds?.length || 1;
        acc.totalAmount += order.totalPrice || 0;
        acc.totalItems += (order.items || []).reduce(
          (sum: number, item: any) => sum + item.quantity,
          0,
        );
        return acc;
      },
      {
        totalCustomers: 0,
        totalOrders: 0,
        totalAmount: 0,
        totalItems: 0,
      },
    );
  }, [aggregatedOrders]);

  if (aggregatedOrders.length === 0) {
    return (
      <div className="py-20 flex flex-col items-center justify-center text-gray-400 gap-2">
        <ShoppingBag size={48} strokeWidth={1} />
        <p className="text-sm font-bold">수령 완료된 구매 내역이 없습니다.</p>
        <p className="text-xs">
          매장형 주문에서 수령완료 처리된 상품이 여기에 표시됩니다.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <div className="rounded-[24px] border border-emerald-100 bg-emerald-50/70 p-4">
          <p className="text-[10px] font-black text-emerald-700 uppercase tracking-wider">
            구매 고객
          </p>
          <p className="text-2xl font-black text-gray-900 mt-2">
            {summary.totalCustomers}명
          </p>
        </div>
        <div className="rounded-[24px] border border-blue-100 bg-blue-50/70 p-4">
          <p className="text-[10px] font-black text-blue-700 uppercase tracking-wider">
            완료 주문
          </p>
          <p className="text-2xl font-black text-gray-900 mt-2">
            {summary.totalOrders}건
          </p>
        </div>
        <div className="rounded-[24px] border border-amber-100 bg-amber-50/80 p-4">
          <p className="text-[10px] font-black text-amber-700 uppercase tracking-wider">
            구매 수량
          </p>
          <p className="text-2xl font-black text-gray-900 mt-2">
            {summary.totalItems}개
          </p>
        </div>
        <div className="rounded-[24px] border border-gray-200 bg-gray-50 p-4">
          <p className="text-[10px] font-black text-gray-500 uppercase tracking-wider">
            구매 금액
          </p>
          <p className="text-2xl font-black text-gray-900 mt-2">
            {summary.totalAmount.toLocaleString()}원
          </p>
        </div>
      </div>

      <div className="hidden lg:block bg-white rounded-[32px] border shadow-sm overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter w-40">
                완료 정보
              </th>
              <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter">
                고객 정보
              </th>
              <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter">
                픽업 정보
              </th>
              <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter">
                구매 품목
              </th>
              <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter text-right">
                구매 금액
              </th>
              <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter text-center w-28">
                상태
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {aggregatedOrders.map((order: any) => {
              const mainItem = order.items?.[0];
              const otherCount = Math.max((order.items?.length || 1) - 1, 0);
              const displayName =
                customerAliases &&
                order.customerPhone &&
                customerAliases[order.customerPhone.replace(/[^0-9]/g, '')]
                  ? `${customerAliases[order.customerPhone.replace(/[^0-9]/g, '')]} (${order.customerName})`
                  : order.customerName;

              return (
                <tr key={order.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-tight">
                        <Calendar size={10} /> {order.date}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs font-black text-gray-900">
                        <StoreIcon size={10} className="text-emerald-500" /> {order.storeName}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() =>
                          onViewHistory?.(
                            order.customerName,
                            order.customerPhone || '',
                            order.userId,
                            order.orderSheetId,
                          )
                        }
                        className="text-sm font-black text-gray-900 hover:text-emerald-600 transition-colors text-left"
                      >
                        {displayName}
                      </button>
                      <span className="text-[10px] text-gray-400 font-bold">
                        {formatPhoneNumber(order.customerPhone)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {order.request ? (
                      <p className="text-[11px] font-bold text-gray-500 whitespace-pre-line leading-relaxed">
                        {order.request}
                      </p>
                    ) : (
                      <span className="text-[11px] font-bold text-gray-300">
                        요청 없음
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {mainItem ? (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg border bg-gray-50 overflow-hidden shrink-0 shadow-inner">
                          <img
                            src={sanitizeImageUrl(mainItem.image)}
                            className="w-full h-full object-cover"
                            alt=""
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-black truncate">{getOrderItemDisplayName(mainItem)}</p>
                          <p className="text-[10px] font-bold text-gray-400">
                            {mainItem.quantity}개
                            {otherCount > 0 ? ` · 외 ${otherCount}건` : ''}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <span className="text-[11px] font-bold text-gray-300">
                        품목 없음
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="text-sm font-black text-gray-900">
                      {order.totalPrice.toLocaleString()}원
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-center">
                      <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[10px] font-black bg-emerald-50 text-emerald-600 border border-emerald-200">
                        <Check size={12} /> 구매 완료
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="lg:hidden space-y-4">
        {aggregatedOrders.map((order: any) => {
          const displayName =
            customerAliases &&
            order.customerPhone &&
            customerAliases[order.customerPhone.replace(/[^0-9]/g, '')]
              ? `${customerAliases[order.customerPhone.replace(/[^0-9]/g, '')]} (${order.customerName})`
              : order.customerName;

          return (
            <div
              key={order.id}
              className="bg-white rounded-[28px] border border-emerald-100 shadow-sm overflow-hidden"
            >
              <div className="px-5 py-4 bg-emerald-50/60 flex justify-between items-start border-b border-emerald-100">
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() =>
                      onViewHistory?.(
                        order.customerName,
                        order.customerPhone || '',
                        order.userId,
                        order.orderSheetId,
                      )
                    }
                    className="text-sm font-black text-gray-900 text-left"
                  >
                    {displayName}
                  </button>
                  <div className="flex items-center gap-1.5 text-[9px] text-gray-500 font-bold uppercase tracking-tight">
                    <Calendar size={10} /> {order.date}
                  </div>
                  <div className="flex items-center gap-1.5 text-[9px] text-gray-500 font-bold uppercase tracking-tight">
                    <StoreIcon size={10} /> {order.storeName}
                  </div>
                  <span className="text-[10px] text-gray-400 font-bold">
                    {formatPhoneNumber(order.customerPhone)}
                  </span>
                </div>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black bg-emerald-100 text-emerald-700">
                  <Check size={11} /> 완료
                </span>
              </div>

              <div className="p-5 space-y-3">
                {order.request && (
                  <div className="rounded-2xl border border-emerald-100 bg-emerald-50/50 px-3 py-3">
                    <p className="text-[11px] font-bold text-gray-600 whitespace-pre-line leading-relaxed">
                      {order.request}
                    </p>
                  </div>
                )}

                {(order.items || []).map((item: any, idx: number) => (
                  <div key={`${order.id}-${idx}`} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg border bg-gray-50 overflow-hidden shrink-0">
                      <img
                        src={sanitizeImageUrl(item.image)}
                        className="w-full h-full object-cover"
                        alt=""
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-black truncate leading-tight mb-0.5">
                        {getOrderItemDisplayName(item)}
                      </p>
                      <p className="text-[10px] font-bold text-gray-400 leading-none">
                        {item.quantity}개 / {item.price.toLocaleString()}원
                      </p>
                    </div>
                    <div className="text-[11px] font-black text-emerald-700">
                      {(item.price * item.quantity).toLocaleString()}원
                    </div>
                  </div>
                ))}
              </div>

              <div className="px-5 py-3 bg-gray-50/30 border-t flex justify-between items-center">
                <div className="flex flex-col">
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-tight leading-none mb-1">
                    구매 금액
                  </span>
                  <span className="text-base font-black text-gray-900">
                    {order.totalPrice.toLocaleString()}원
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tight leading-none">
                    구매 품목
                  </span>
                  <p className="text-sm font-black text-emerald-700 mt-1">
                    {(order.items || []).reduce(
                      (sum: number, item: any) => sum + item.quantity,
                      0,
                    )}
                    개
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
